import { useMemo, useState } from 'react';
import { DiffEditor } from '@monaco-editor/react';
import { I18n } from '@/i18n/I18n';
import { relativeTime, shortName } from '@/frontend/lib/people';
import { cn } from '@/frontend/lib/utils';
import { canonical } from '@/reports/bundles/inputSheet';
import { InputSheet } from '@/reports/inputs';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { readSheetFile } from '../inputs/InputsTab';
import { FileView, ViewSwitch } from './designerChrome';

/** One way two sheets differ: an input or a section added, removed, changed or moved. */
export interface SheetDifference {
  id: string;
  label: string;
  what: 'input' | 'section';
  change: 'added' | 'removed' | 'changed' | 'moved';
  /** changed: the properties that differ; moved: the section it was in, and the one it is in now (none when it only changed place among the others) */
  detail?: string[];
  /** the section it is in (or was, when removed) */
  section?: string;
}

// however the keys were ordered: jsonb reorders them
const same = (a: unknown, b: unknown) => canonical(a) === canonical(b);

/** The ids, of those in both, that changed place among the others: the fewest that, moved, turn one order into the other. */
function outOfOrder(before: string[], after: string[]): Set<string> {
  const kept = new Set(after);
  const a = before.filter((id) => kept.has(id));
  const had = new Set(a);
  const b = after.filter((id) => had.has(id));
  // the longest run of ids in the same order in both stays put; the rest moved
  const runs = a.map(() => new Array<number>(b.length + 1).fill(0));
  runs.push(new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) runs[i][j] = a[i] === b[j] ? runs[i + 1][j + 1] + 1 : Math.max(runs[i + 1][j], runs[i][j + 1]);
  }
  const stayed = new Set<string>();
  for (let i = 0, j = 0; i < a.length && j < b.length;) {
    if (a[i] === b[j]) {
      stayed.add(a[i]);
      i++;
      j++;
    } else if (runs[i + 1][j] >= runs[i][j + 1]) i++;
    else j++;
  }
  return new Set(b.filter((id) => !stayed.has(id)));
}

/** What `mine` changes of `theirs`, input by input and section by section, matched by id. */
export function sheetDifferences(theirs: InputSheet, mine: InputSheet): SheetDifference[] {
  const out: SheetDifference[] = [];
  const theirSections = new Map(theirs.sections.map((s) => [s.id, s]));
  const mySections = new Map(mine.sections.map((s) => [s.id, s]));
  for (const section of mine.sections) {
    const before = theirSections.get(section.id);
    if (!before) out.push({ id: section.id, label: section.title, what: 'section', change: 'added' });
    else {
      const changed = Object.keys({ ...before, ...section }).filter((k) => k !== 'inputs' && !same((before as unknown as Record<string, unknown>)[k], (section as unknown as Record<string, unknown>)[k]));
      if (changed.length) out.push({ id: section.id, label: section.title, what: 'section', change: 'changed', detail: changed });
    }
  }
  for (const section of theirs.sections) if (!mySections.has(section.id)) out.push({ id: section.id, label: section.title, what: 'section', change: 'removed' });
  for (const id of outOfOrder(theirs.sections.map((s) => s.id), mine.sections.map((s) => s.id))) {
    out.push({ id, label: mySections.get(id)?.title ?? id, what: 'section', change: 'moved' });
  }

  const placed = (sheet: InputSheet) => new Map(sheet.sections.flatMap((s) => s.inputs.map((input) => [input.id, { input: input as unknown as Record<string, unknown> & { label: string }, section: s }] as const)));
  const theirInputs = placed(theirs);
  const myInputs = placed(mine);
  for (const [id, { input, section }] of myInputs) {
    const before = theirInputs.get(id);
    if (!before) {
      out.push({ id, label: input.label, what: 'input', change: 'added', section: section.title });
      continue;
    }
    const changed = Object.keys({ ...before.input, ...input }).filter((k) => !same(before.input[k], input[k]));
    if (changed.length) out.push({ id, label: input.label, what: 'input', change: 'changed', detail: changed, section: section.title });
    if (before.section.id !== section.id) out.push({ id, label: input.label, what: 'input', change: 'moved', detail: [before.section.title, section.title], section: section.title });
  }
  for (const [id, { input, section }] of theirInputs) if (!myInputs.has(id)) out.push({ id, label: input.label, what: 'input', change: 'removed', section: section.title });
  // inputs that stayed in their section but changed place in it
  for (const section of mine.sections) {
    const before = theirSections.get(section.id);
    if (!before) continue;
    for (const id of outOfOrder(before.inputs.map((i) => i.id), section.inputs.map((i) => i.id))) {
      out.push({ id, label: myInputs.get(id)?.input.label ?? id, what: 'input', change: 'moved', section: section.title });
    }
  }
  return out;
}

const TONE: Record<SheetDifference['change'], string> = {
  added: 'bg-emerald-100 text-emerald-900',
  removed: 'bg-red-100 text-red-800',
  changed: 'bg-amber-100 text-amber-900',
  moved: 'bg-sky-100 text-sky-900',
};

/**
 * The schema's draft as saved elsewhere since this page opened it, beside what
 * is typed here: what yours changes of theirs, input by input, or the two
 * files side by side as code. Either is kept from here.
 */
export function CompareSheetsDialog({ title, theirs, mine, by, at, busy, onTheirs, onMine, onHide }: {
  /** the schema's */
  title: string;
  /** inputs.json as saved elsewhere, and as typed here */
  theirs: string;
  mine: string;
  /** who saved theirs, and when, when known */
  by?: string | null;
  at?: Date | string | null;
  busy?: boolean;
  onTheirs: () => void;
  onMine: () => void;
  onHide: () => void;
}) {
  const theirSheet = useMemo(() => readSheetFile(theirs).sheet, [theirs]);
  const mySheet = useMemo(() => readSheetFile(mine).sheet, [mine]);
  const differences = useMemo(() => (theirSheet && mySheet ? sheetDifferences(theirSheet, mySheet) : null), [theirSheet, mySheet]);
  const [view, setView] = useState<FileView>(differences ? 'visual' : 'code');
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onHide(); }}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{I18n.get('designerCompareTitle', { title })}</DialogTitle>
          <DialogDescription>
            {by ? I18n.get('designerCompareBy', { who: shortName(by), ago: at ? relativeTime(at) : '' }) : I18n.get('designerCompareHelp')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2">
          <ViewSwitch views={differences ? ['visual', 'code'] : ['code']} value={view} onChange={setView} />
          {view === 'code' && <span className="text-xs text-muted-foreground">{I18n.get('designerCompareSides')}</span>}
        </div>
        {view === 'visual' && differences ? (
          differences.length === 0 ? (
            <p className="text-sm text-muted-foreground">{I18n.get('designerCompareSame')}</p>
          ) : (
            <ul aria-label={I18n.get('designerCompareDifferences')} className="max-h-[55vh] divide-y overflow-y-auto rounded-lg border-[1px]">
              {differences.map((d) => (
                <li key={`${d.what}:${d.id}:${d.change}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-3 py-2 text-sm">
                  <span className={cn('rounded-sm px-1.5 text-[0.7rem] font-semibold', TONE[d.change])}>{I18n.get(`designerCompare_${d.change}`)}</span>
                  <span className="font-medium">{d.label || d.id}</span>
                  <span className="font-mono text-xs text-muted-foreground">{d.what === 'section' ? I18n.get('designerCompareSection') : d.id}</span>
                  {d.change === 'changed' && d.detail && <span className="text-xs text-muted-foreground">{I18n.get('designerCompareChanged', { what: d.detail.join(', ') })}</span>}
                  {d.change === 'moved' && <span className="text-xs text-muted-foreground">{d.detail ? I18n.get('designerCompareMoved', { from: d.detail[0], to: d.detail[1] }) : I18n.get('designerCompareReordered')}</span>}
                  {d.what === 'input' && !(d.change === 'moved' && d.detail) && d.section && <span className="ml-auto text-xs text-muted-foreground">{d.section}</span>}
                </li>
              ))}
            </ul>
          )
        ) : (
          <div className="h-[55vh] overflow-hidden rounded-lg border-[1px]" aria-label={I18n.get('designerCompareCode')}>
            <DiffEditor height="100%" language="json" original={theirs} modified={mine} theme="vs-light" options={{ readOnly: true, originalEditable: false, renderSideBySide: true, minimap: { enabled: false }, scrollBeyondLastLine: false, automaticLayout: true }} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onHide} disabled={busy}>{I18n.get('cancel')}</Button>
          <Button variant="outline" onClick={onTheirs} disabled={busy}>{I18n.get('schemaDraftConflictTheirs')}</Button>
          <Button onClick={onMine} disabled={busy}>{I18n.get('rv2SheetConflictMine')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
